// src/pages/onboarding/ArticlesPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjects } from '../../contexts/ProjectContext';
import {
  LayoutDashboard,
  Calendar,
  Clock,
  Settings,
  Link as LinkIcon,
  ImagePlus,
  Users
} from 'lucide-react';
import api from '../../utils/apiClient';

// Image placeholder component
const ImageStyleOption = ({ title, selected, onClick, children }) => (
  <div
    onClick={onClick}
    className={`
      cursor-pointer rounded-lg overflow-hidden transition-all
      ${selected ? 'ring-2 ring-primary-500 ring-offset-2' : 'hover:shadow-lg'}
    `}
  >
    <div className="bg-gray-100 aspect-square flex items-center justify-center overflow-hidden">
      {children}
    </div>
    <div className="p-2 text-center">
      <p className="text-sm font-medium">{title}</p>
    </div>
  </div>
);

const ArticlesPage = ({ isSettings = false }) => {
  const navigate = useNavigate();
  const { currentProject, loadCurrentProject } = useProjects();

  // form state
  const [language, setLanguage]     = useState('English');
  const [toneStyle, setToneStyle]   = useState('Informative');
  const [links, setLinks]           = useState('3 Links');
  const [addVideo, setAddVideo]     = useState('Yes');
  const [addCta, setAddCta]         = useState('Yes');
  const [imageStyle, setImageStyle] = useState('Brand & Text Realism');

  const [loading, setLoading]       = useState(false);
  const [errorMsg, setErrorMsg]     = useState('');

  // Preload from server whenever the loaded project changes (fixes Settings race with cached context)
  useEffect(() => {
    if (!currentProject?.id) return;
    const {
      blog_language,
      blog_tone,
      suggested_internal_links,
      add_youtube_video,
      add_cta,
      image_style
    } = currentProject;

    if (blog_language) setLanguage(blog_language);
    if (blog_tone) setToneStyle(blog_tone);
    if (typeof suggested_internal_links === 'number') {
      setLinks(`${suggested_internal_links} Links`);
    }
    if (add_youtube_video != null) setAddVideo(add_youtube_video ? 'Yes' : 'No');
    if (add_cta != null) setAddCta(add_cta ? 'Yes' : 'No');
    if (image_style) setImageStyle(image_style);
  }, [currentProject?.id]);

  // dropdown toggles
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showToneDropdown, setShowToneDropdown]         = useState(false);
  const [showLinksDropdown, setShowLinksDropdown]       = useState(false);
  const [showVideoDropdown, setShowVideoDropdown]       = useState(false);
  const [showCtaDropdown, setShowCtaDropdown]           = useState(false);

  const imageStyles = [
    { id: 'brand', title: 'Brand & Text Realism' },
    { id: 'watercolor', title: 'Watercolor Realism' },
    { id: 'cinematic', title: 'Cinematic Realism' },
    { id: 'illustration', title: 'Illustration' },
    { id: 'sketch', title: 'Sketch' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (!currentProject?.id) {
      setErrorMsg('No project selected');
      return;
    }

    setLoading(true);
    try {
      // map "3 Links" → 3
      const numLinks = parseInt(links, 10) || 0;

      await api.projects.updateBloggingStyle(currentProject.id, {
        blog_language:            language,
        blog_tone:                toneStyle,
        suggested_internal_links: numLinks,
        add_youtube_video:        addVideo === 'Yes',
        add_cta:                  addCta === 'Yes',
        image_style:              imageStyle,
        // brand_color left untouched
      });

      await loadCurrentProject(currentProject.id, { force: true });

      if (!isSettings) {
        navigate('/onboarding/keywords');
      }
    } catch (err) {
      console.error(err);
      setErrorMsg(err.response?.data?.error || err.message || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 md:p-8">
      {!isSettings && (
        <h1 className="text-2xl font-bold text-center text-gray-900 mb-8">
          Set up your Articles
        </h1>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Content Section */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Content</h2>
            <div className="space-y-6">
              {/* Language */}
              <DropdownField
                label="Language"
                value={language}
                options={['English','Spanish','French','German','Chinese']}
                open={showLanguageDropdown}
                setOpen={setShowLanguageDropdown}
                onSelect={val => setLanguage(val)}
              />

              {/* Tone & Style */}
              <DropdownField
                label="Tone and Style"
                value={toneStyle}
                options={['Informative','Conversational','Professional','Friendly','Technical']}
                open={showToneDropdown}
                setOpen={setShowToneDropdown}
                onSelect={val => setToneStyle(val)}
                help="Finetune With Your Articles"
              />
            </div>
          </div>

          {/* SEO Section */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">SEO</h2>
            <DropdownField
              label="Internal Links"
              value={links}
              options={['No Links','1 Link','2 Links','3 Links','4 Links','5 Links']}
              open={showLinksDropdown}
              setOpen={setShowLinksDropdown}
              onSelect={val => setLinks(val)}
            />
          </div>
        </div>

        {/* Enhancements */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Enhancements</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <DropdownField
              label="Add YouTube Video"
              value={addVideo}
              options={['Yes','No']}
              open={showVideoDropdown}
              setOpen={setShowVideoDropdown}
              onSelect={val => setAddVideo(val)}
            />
            <DropdownField
              label="Add CTA"
              value={addCta}
              options={['Yes','No']}
              open={showCtaDropdown}
              setOpen={setShowCtaDropdown}
              onSelect={val => setAddCta(val)}
            />
          </div>

          {/* Image Style */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Image Style
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {imageStyles.map(s => (
                <ImageStyleOption
                  key={s.id}
                  title={s.title}
                  selected={imageStyle === s.title}
                  onClick={() => setImageStyle(s.title)}
                >
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400">
                    <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                </ImageStyleOption>
              ))}
            </div>
          </div>
        </div>

        {errorMsg && (
          <div className="bg-red-100 text-red-700 p-2 rounded">{errorMsg}</div>
        )}

        <div className="flex justify-center">
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 bg-primary-500 text-white rounded-md hover:bg-primary-600 disabled:opacity-50 transition"
          >
            {loading ? 'Saving…' : isSettings ? 'Save Changes' : 'Continue'}
          </button>
        </div>
      </form>
    </div>
  );
};

// Helper dropdown field component
function DropdownField({ label, value, options, open, setOpen, onSelect, help }) {
  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        {help && <span className="text-xs text-gray-500">{help}</span>}
      </div>
      <div className="relative">
        <button
          type="button"
          className="w-full bg-white border border-gray-300 rounded-md shadow-sm px-4 py-2 text-left text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          onClick={() => setOpen(o => !o)}
        >
          <span className="flex items-center justify-between">
            <span>{value}</span>
            <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </span>
        </button>
        {open && (
          <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md py-1">
            {options.map(opt => (
              <button
                key={opt}
                type="button"
                className={`w-full text-left px-4 py-2 text-sm ${
                  opt === value ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => {
                  onSelect(opt);
                  setOpen(false);
                }}
              >
                {opt}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ArticlesPage;
